import type { IErrorData, ISuccessData } from '@/server/api/self-service/types'
import type { IBookingVerificationResponse } from '@/storyblok/types'
import { CKI, MMB } from '@/utils/constants/rip'

const EVENT_NAME = 'index_page_events'

function pushIndexPageErrorDataLayer(data: IBookingVerificationResponse) {
  const errorMessage =
    data.errorRef && data.errorRef !== '' ? `${data.serverError} (ref: ${data.errorRef})` : data.serverError
  const refData = data.ref ? { ref: data.ref } : {}

  pushToDataLayer({
    event: data.event,
    event_category: data.eventCategory,
    event_action: data.eventAction,
    error_message: errorMessage,
    error_category: data.errorCategory,
    origin_airport_code: data.originAirportCode,
    destination_airport_code: data.destinationAirportCode,
    departure_date: data.departureDate,
    return_date: data.returnDate,
    noa: data.noa,
    noi: data.noi,
    noc: data.noc,
    ...refData
  })
}

function pushBookingVerificationPageLoadErrorDataLayer({ data }: { data: IBookingVerificationResponse }) {
  const errorMessage =
    data.errorRef && data.errorRef !== '' ? `${data.serverError} (ref: ${data.errorRef})` : data.serverError
  const refData = data.ref ? { ref: data.ref } : {}

  if (!data.event || !errorMessage) return

  pushToDataLayer({
    event: data.event,
    event_category: data.eventCategory,
    event_action: data.eventAction,
    error_message: errorMessage,
    error_category: data.errorCategory || '',
    origin_airport_code: data.originAirportCode || '',
    destination_airport_code: data.destinationAirportCode || '',
    departure_date: data.departureDate || '',
    return_date: data.returnDate || '',
    noa: data.noa,
    noi: data.noi,
    noc: data.noc,
    ...refData
  })
}

export const useBookingVerificationDataLayer = () => {
  const { locale } = useI18n()
  const route = useRoute()
  const dayjs = useDayjs()

  function formatSegmentDate(date?: string) {
    return date ? dayjs(date).format('YYYYMMDD') : ''
  }

  function formatSegmentTime(date?: string) {
    return date ? dayjs(date).format('HH:mm') : ''
  }

  function pushBookingVerificationDataLayer(formType: string) {
    pushToDataLayer({
      event: EVENT_NAME,
      generic_event_name: `${formType}_login`,
      event_category: 'successful',
      site_language: locale.value,
      page_type: `index_${formType}`
    })
  }

  function pushBookingVerificationErrorDataLayer({
    responseData,
    errorAlertText,
    formType,
    matchedErrorCategory
  }: {
    responseData: ISuccessData | IErrorData
    errorAlertText?: string[]
    formType: string
    matchedErrorCategory?: string
  }) {
    let event: string | null = null

    if (formType === CKI) {
      event = 'ckin_index_error'
    } else if (formType === MMB) {
      event = 'mmb_index_errors'
    }

    if (!event) return

    const directions = 'directions' in responseData ? responseData.directions : []
    const apiErrors = 'errors' in responseData ? responseData.errors : []
    const errorData = 'code' in responseData ? responseData : null
    const outboundSegments = directions[0]?.segments ?? []
    const inboundSegments = directions[1]?.segments ?? []
    const firstOutboundSegment = outboundSegments[0]
    const lastOutboundSegment = outboundSegments.at(-1)
    const firstInboundSegment = inboundSegments[0]
    const connectingAirportCode = outboundSegments
      .slice(0, -1)
      .map((segment) => segment.dest)
      .join(',')
    const resolvedErrorMessage = errorAlertText ?? []
    const serverError = resolvedErrorMessage[0] || errorData?.message || apiErrors[0]?.message || ''
    const errorCategory = matchedErrorCategory || errorData?.code || apiErrors[0]?.errorCode || ''

    pushToDataLayer({
      event,
      event_category: 'red',
      event_action: route.path,
      error_message: serverError,
      error_category: errorCategory,
      origin_airport_code: firstOutboundSegment?.orig || '',
      destination_airport_code: lastOutboundSegment?.dest || '',
      connecting_airport_code: connectingAirportCode,
      departure_date: formatSegmentDate(firstOutboundSegment?.depart),
      departure_flight_time: formatSegmentTime(firstOutboundSegment?.depart),
      return_date: formatSegmentDate(firstInboundSegment?.depart),
      return_flight_time: formatSegmentTime(firstInboundSegment?.depart)
    })
  }

  return {
    pushBookingVerificationDataLayer,
    pushBookingVerificationErrorDataLayer,
    pushBookingVerificationPageLoadErrorDataLayer,
    pushIndexPageErrorDataLayer
  }
}
